// "Connected apps" on /account: the apps a person has let use their account
// through the OAuth server (backend/oauth/), and a way to disconnect each one.
//
// Disconnecting forgets the approval AND revokes every token the app holds, so
// it stops working at once rather than when its current token expires.
//
// Its own component, fetching its own data, so AccountPage gains one line and
// nothing it already renders moves.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConnectedApps() {
  const [connections, setConnections] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/oauth/connections`, { withCredentials: true })
      .then((r) => { if (live) setConnections(r.data.connections || []); })
      .catch(() => { if (live) { setFailed(true); setConnections([]); } });
    return () => { live = false; };
  }, []);

  const disconnect = async (clientId) => {
    setBusy(clientId);
    try {
      await axios.post(`${API}/oauth/connections/disconnect`, { client_id: clientId },
        { withCredentials: true });
      setConnections((list) => list.filter((c) => c.client_id !== clientId));
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
      <h2 className="font-semibold mb-2" style={{ color: 'var(--ink-1)' }}>Connected apps</h2>
      <p className="text-sm mb-4" style={{ color: 'var(--ink-2)' }}>
        Apps you have allowed to use your UXAuditPro account, such as Claude or ChatGPT.
      </p>
      {connections === null ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--ink-2)' }} aria-label="Loading connected apps" />
        </div>
      ) : failed ? (
        // A failed fetch is UNKNOWN, never "you have no apps": that would tell
        // someone checking for a stray connection that there is none.
        <p className="text-sm" role="alert" style={{ color: 'var(--danger)' }}>
          Connected apps could not be loaded. Refresh the page to try again.
        </p>
      ) : connections.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--ink-2)' }}>No apps are connected.</p>
      ) : (
        <ul className="space-y-3">
          {connections.map((c) => (
            <li key={c.client_id} className="flex items-start justify-between gap-4 py-3" style={{ borderTop: '1px solid var(--border-1)' }}>
              <div className="min-w-0">
                <p className="text-sm font-medium" style={{ color: 'var(--ink-1)' }}>{c.client_name}</p>
                <ul className="text-xs mt-1 space-y-0.5" style={{ color: 'var(--ink-2)' }}>
                  {c.scopes.map((s) => <li key={s.scope}>{s.description}</li>)}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => disconnect(c.client_id)}
                disabled={busy === c.client_id}
                className="uxa-submit uxa-submit--auto uxa-submit--tertiary flex-none"
                aria-label={`Disconnect ${c.client_name}`}
              >
                {busy === c.client_id ? 'Disconnecting' : 'Disconnect'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
